import { BadgePercent, FileText, Phone, Boxes, ListChecks, Users, Receipt, MessageCircleHeart } from "lucide-react";
import { useSeo } from "../lib/site";
import { SeniorTerms, CallButton, QuoteButton, FinalCta } from "../components/site/Blocks";

const POINTS = [
  [BadgePercent, "Lifetime senior discount", "20% off eligible services on every visit, for life."],
  [FileText, "Clear written estimates", "Itemized, in plain language, with your savings shown separately."],
  [MessageCircleHeart, "Patient communication", "We take the time to listen and answer every question."],
  [Phone, "Quote by phone", "No smartphone, email or photos needed. Just call and talk it through."],
  [Boxes, "Decluttering & downsizing", "Help sorting what stays, what's donated and what goes — at your pace."],
  [ListChecks, "Small-task visits", "One visit to handle approved small tasks on your list."],
  [Users, "Family coordination", "Optional — only with your permission."],
  [Receipt, "Pay per visit", "No subscription and no membership required."],
];

export default function Senior() {
  useSeo("Senior Home Support", "Dependable, respectful help for Albuquerque seniors: decluttering, downsizing and small approved tasks, with a lifetime 20% discount on eligible services and phone-based quotes.");
  return (
    <div data-testid="senior-page">
      <section className="bg-[#F5F5F5]">
        <div className="wrap grid gap-10 py-14 sm:py-20 lg:grid-cols-[1.2fr_0.8fr]">
          <div className="fade-up">
            <p className="eyebrow">Senior Home Support</p>
            <h1 className="mt-3 text-4xl font-extrabold uppercase leading-[1.05] sm:text-5xl lg:text-6xl">Dependable Help for Your Home—and Peace of Mind for Your Family.</h1>
            <p className="mt-5 max-w-2xl text-xl leading-relaxed text-neutral-700">Respectful help that keeps you in charge of your home. You decide what gets done, and when.</p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <CallButton label="Call for a phone estimate" testid="senior-call-btn" />
              <QuoteButton className="btn-outline" label="Request online" testid="senior-quote-btn" />
            </div>
          </div>
          <div className="rounded-lg border-4 border-[#D9232E] bg-white p-7" data-testid="senior-offer-box">
            <p className="font-display text-5xl font-extrabold uppercase leading-none text-[#D9232E]">20% off<span className="block text-3xl text-[#171717]">for life</span></p>
            <p className="mt-3 text-lg font-bold">For senior customers, on eligible services.</p>
            <div className="mt-4"><SeniorTerms /></div>
            <a href="/terms#senior" className="mt-4 inline-block font-bold text-[#B81B25] underline" data-testid="senior-terms-link">Read the full discount terms</a>
          </div>
        </div>
      </section>
      <section className="wrap py-16">
        <h2 className="h2">What you can expect</h2>
        <div className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {POINTS.map(([Icon, t, d]) => (
            <div key={t} className="card">
              <Icon className="h-8 w-8 text-[#D9232E]" aria-hidden="true" />
              <h3 className="mt-3 text-xl font-bold uppercase">{t}</h3>
              <p className="mt-1 text-base text-neutral-700">{d}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="bg-[#171717] text-white" data-testid="senior-family-section">
        <div className="wrap py-16">
          <h2 className="h2">Arranging help for Mom or Dad?</h2>
          <p className="mt-4 max-w-3xl text-xl leading-relaxed text-neutral-200">Tell us what's needed. With their permission, we can coordinate appointments and share completion updates.</p>
          <QuoteButton className="btn-red mt-8" label="Request help for a family member" testid="senior-family-quote-btn" />
        </div>
      </section>
      <section className="wrap py-12">
        <p className="max-w-3xl text-base text-neutral-600">One Punch Services provides household help such as decluttering, hauling and small approved tasks. We are not a medical or home-care provider, a safety inspector, or a certified aging-in-place specialist.</p>
      </section>
      <FinalCta />
    </div>
  );
}
